import { useCallback } from 'react';
import { logEventString } from '@adv-frontend/yastore-modules/logger';
import { type Page } from '#shared/api/schema';
import { alertSrc } from '#shared/assets';
import { useConfirm } from '#shared/lib/hooks';
import { CONSTRUCTOR_KEY, persistentStorage } from '#shared/lib/storage';
import { format } from '#shared/lib/string';
import { constructorTestIds } from '#shared/lib/testids';
import { constructorProductLoggingEvents } from '../lib/constructorProductLoggingEvents';
import {
    changeActiveVersion,
    changeConstructorGlobalSettings,
    changePageSettings,
    useConstructorIsFirstTime,
} from '../model/slices';
import { useChangeConstructorRightBarSection } from './useChangeConstructorRightBarSection';
import { useConstructorMigrateGlobalSettings } from './useConstructorMigrateGlobalSettings';

const oneWeekMs = 1000 * 60 * 60 * 24 * 8;

export const useApplyConstructorLocalChanges = () => {
    const { confirm, renderConfirmModal } = useConfirm();
    const changeConstructorRightBarSection = useChangeConstructorRightBarSection();
    const migrateGlobalSettings = useConstructorMigrateGlobalSettings();
    const isFirstTime = useConstructorIsFirstTime();

    const checkForChanges = useCallback(async () => {
        const settings = persistentStorage.getItem(CONSTRUCTOR_KEY);

        if (!settings || isFirstTime) {
            return;
        }

        const { pages, globalSettings, date, versionId } = JSON.parse(settings);

        if (Date.now() - date < oneWeekMs && (globalSettings || pages?.length > 0)) {
            const isConfirmed = await confirm({
                imageSrc: alertSrc,
                title: 'Есть несохранённые изменения в конструкторе',
                bodyText: `Изменения от ${format(date, 'pretty-datetime')}`,
                applyText: 'Восстановить изменения',
                cancelText: 'Не восстанавливать',
                'data-testid': constructorTestIds.localChangesConfirmModal,
                applyButtonTestId: constructorTestIds.localChangesConfirm,
            });

            if (isConfirmed) {
                logEventString(constructorProductLoggingEvents.LocalSave.Restore, 'true');

                if (globalSettings) {
                    changeConstructorGlobalSettings(migrateGlobalSettings(globalSettings));
                }

                if (pages?.length > 0) {
                    pages.forEach((page: Page) => {
                        changePageSettings({ pageId: page.id, pageSettings: page });
                    });
                }

                changeActiveVersion(versionId);
                changeConstructorRightBarSection();
            } else {
                logEventString(constructorProductLoggingEvents.LocalSave.Discard, 'true');

                persistentStorage.removeItem(CONSTRUCTOR_KEY);
            }
        }
    }, [confirm, changeConstructorRightBarSection, migrateGlobalSettings, isFirstTime]);

    return { checkForChanges, renderConfirmModal };
};
